/**
 * The shell: left rail, top bar, content.
 *
 * Navigation is grouped by the JOB someone came to do — Monitor / Compose / Infrastructure /
 * Developer — rather than as one flat list. Every item from the approved list is present; the grouping
 * is what keeps it navigable as the product grows past a dozen screens.
 *
 * The rail is fixed and does not reorder itself. Predictability beats cleverness in a tool someone
 * opens every day; muscle memory is the entire value of stable navigation.
 *
 * ═══ Nothing in this shell pretends to work ══════════════════════════════════
 *
 * Every control here is either wired to something real or VISIBLY INERT — muted, non-interactive,
 * and labelled "Not built yet". An earlier version had a convincing global search box, a workspace
 * switcher and a product switcher that all did nothing when clicked. A dead control is worse than an
 * absent one: the person cannot tell whether they misunderstood the product or hit a bug, and they
 * lose trust in the controls that DO work.
 *
 * The environment switch was the serious one. It flipped a client-side flag and displayed
 * "Messages are not delivered to real recipients" — while the server has no idea the switch exists,
 * so mail would have gone out for real. A UI that tells someone they are in a sandbox when they are
 * not is how a test campaign reaches live customers. It is inert until the server honours it.
 */
import { useEffect, useState, type ReactNode } from "react";
import { AnnouncementBanner } from "./AnnouncementBanner";
import { BrandMark } from "./BrandMark";
import { Link, useLocation } from "wouter";
import { Icon, type IconName } from "./Icon";
import { identity, signOut } from "../lib/session";

interface NavItem {
  label: string;
  href: string;
  icon: IconName;
  /** Items awaiting the user. A number here means "these need you", not "these exist". */
  count?: number;
  /** Something that will break sending if ignored. */
  warn?: number;
  external?: boolean;
  /** Not built yet — shown, but honestly. */
  pending?: boolean;
  /** Why it is inert, when "the screen is not built" is not the real reason. */
  reason?: string;
}

interface NavGroup {
  title?: string;
  items: NavItem[];
}

/*
 * `pending` is on every screen that is not built yet, and those links are visibly inert rather than
 * hidden. Hiding them would misrepresent the product's shape; letting them navigate to a blank page
 * would be worse. This is the honest middle, and each one disappears as its screen lands.
 */
const NAV: NavGroup[] = [
  { items: [{ label: "Overview", href: "/", icon: "overview" }] },
  {
    title: "Monitor",
    items: [
      { label: "Logs", href: "/logs", icon: "logs" },
      { label: "Events", href: "/events", icon: "events" },
      { label: "Suppressions", href: "/suppressions", icon: "suppressions" },
    ],
  },
  {
    title: "Compose",
    items: [
      /*
       * Mailboxes is NOT a separate rail item. It lives on the Addresses screen (Infrastructure),
       * which is where receiving belongs alongside domains and routing — and it was appearing twice
       * in the rail (a "Mailboxes" entry here in Compose AND "Addresses" below), for the same screen.
       * The /mailboxes route still resolves for a direct link; it simply is not duplicated in the nav.
       */
      { label: "Templates", href: "/templates", icon: "templates" },
      { label: "Campaigns", href: "/campaigns", icon: "campaigns" },
      { label: "Automations", href: "/automations", icon: "automations" },
    ],
  },
  {
    /*
     * Audience — WHO the mail goes to, distinct from Compose (what it says) and Monitor (what
     * happened). Contacts is the store; Lists are sets someone curated; Segments are saved queries
     * over the store. They sit here, right after Compose, because building a campaign or an
     * automation is where a person reaches for them.
     */
    title: "Audience",
    items: [
      { label: "Contacts", href: "/contacts", icon: "contacts" },
      { label: "Lists", href: "/lists", icon: "lists" },
      { label: "Segments", href: "/segments", icon: "segments" },
    ],
  },
  {
    title: "Infrastructure",
    items: [
      /*
       * Configuration sits FIRST in this group and above Domains deliberately.
       *
       * It is the screen that explains how the rest of the group relates — domain, DNS, sending,
       * receiving and mailboxes as one chain — so someone who does not yet know which of those is
       * their problem lands here and is pointed at the one that is. Putting it after Domains would
       * mean the overview of a section appears halfway down it.
       */
      { label: "Configuration", href: "/configuration", icon: "overview" },
      { label: "Domains", href: "/domains", icon: "domains" },
      { label: "Addresses", href: "/addresses", icon: "addresses" },
      { label: "Inbound & routing", href: "/routing", icon: "routing" },
    ],
  },
  {
    /*
     * Account — what the workspace is paying for.
     *
     * Its own group rather than an item under Infrastructure: a plan is not a piece of sending
     * infrastructure, and burying billing inside a technical section is how customers fail to find
     * out why the product started refusing them.
     */
    title: "Account",
    items: [
      { label: "Plans", href: "/plans", icon: "overview" },
      /*
       * Team sits under Account beside Plans, not under Infrastructure. Both answer "who and what is
       * this workspace" rather than "how does mail leave it", and an owner looking for how to let a
       * colleague run their email looks near billing, not near DNS.
       */
      { label: "Team", href: "/team", icon: "team" },
    ],
  },
  {
    // "Developer", not "Develop". The other three headings name a job you are doing — Monitor,
    // Compose, Infrastructure — but this one named an imperative verb aimed at the reader, which read
    // as an instruction rather than as a category. It holds the things a developer integrating with
    // the API needs.
    title: "Developer",
    items: [
      { label: "API keys", href: "/keys", icon: "keys" },
      { label: "Webhooks", href: "/webhooks", icon: "webhooks" },
      /*
       * This linked to `/docs/email/email-api.md` — a path in the REPOSITORY, not a URL that exists
       * anywhere. On the console's own host the static server correctly refuses it, so the link
       * produced a bare "not found" in plain text on a blank page: the worst possible answer, because
       * it looks like the whole console has crashed. The API origin appears to answer it only because
       * that host serves the marketing SPA shell for any unknown path, which would have rendered the
       * wrong application.
       *
       * `docs/email/email-api.md` is the real reference and it is not published anywhere yet. So this
       * is inert until it is, which is the rule the rest of this shell already follows.
       */
      /*
       * Opens the PUBLIC reference on the marketing site, in a new tab.
       *
       * It was briefly a screen in this console, rendering the reference document inline. That
       * was the wrong home: a developer deciding whether to build on PulseAssist Email had to
       * create an account and sign in to read it, and a reference that cannot be linked or
       * indexed is not doing its job. The nav item stays — this is where a developer looks for
       * it — but it now leaves for the published page.
       */
      {
        label: "API reference",
        href: "https://getpulseassist.com/docs/email-api",
        icon: "docs",
        external: true,
      },
    ],
  },
];

function useTheme() {
  const [theme, setTheme] = useState<"light" | "dark">(
    () => (document.documentElement.dataset.theme as "light" | "dark") ?? "light",
  );
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem("pa-email-theme", theme);
    } catch {
      /* private mode — the theme still applies for this session */
    }
  }, [theme]);
  return { theme, toggle: () => setTheme((t) => (t === "light" ? "dark" : "light")) };
}

/**
 * A control that will exist but does not yet.
 *
 * Rendered as a non-interactive span rather than a disabled button so it cannot be focused, clicked
 * or submitted, and it carries the reason on hover. This is the single treatment used for every
 * unbuilt affordance in the shell, so "muted and slightly faded" reliably means "not yet" rather
 * than meaning something different in each corner of the UI.
 */
/**
 * Move between PulseAssist AI and PulseAssist Email.
 *
 * ═══ Why this exists ══════════════════════════════════════════════════════════
 *
 * ENICE is one platform with two products, and this header is the only place that says which one you
 * are in. It used to render a chevron with no menu behind it, so the single most discoverable control
 * in the app did nothing — people click a chevron, nothing happens, and they conclude the product is
 * half-built.
 *
 * AI lives on a different origin (the main app), so that entry is a real navigation rather than a
 * route: a wouter <Link> to another origin lands on an in-app 404. Email is the current product and is
 * marked as such instead of being offered as a destination.
 *
 * Closes on outside click and Escape, like the account menu — a menu that can only be closed by
 * clicking its trigger again is one people leave open by accident.
 */
function ProductSwitcher() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!(e.target as HTMLElement)?.closest?.("[data-product-switcher]")) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span className="relative ml-auto flex items-center" data-product-switcher="">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Switch product"
        className="flex items-center"
        style={{ color: "var(--muted)" }}
      >
        <Icon name="chevron-down" size={14} />
      </button>
      {open && (
        <span
          role="menu"
          className="absolute right-0 top-full z-50 mt-1.5 flex min-w-[230px] flex-col p-1"
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-lg)",
            // The product's modal elevation — a crisp, token-based drop shadow rather than a raw
            // rgba value that renders differently between the themes.
            boxShadow: "var(--shadow-modal)",
          }}
        >
          <span
            className="px-2 pb-1 pt-1.5"
            style={{ fontSize: "var(--text-meta)", color: "var(--muted)" }}
          >
            ENICE products
          </span>

          {/* The current product. Shown, not offered — a menu item that reloads the page you are on
              is a dead end people click twice. */}
          <span
            role="menuitem"
            aria-current="true"
            className="flex items-center justify-between gap-2 px-2 py-1.5"
            style={{ fontSize: "var(--text-dense)", borderRadius: 4, background: "var(--canvas)" }}
          >
            <span>PulseAssist <b style={{ color: "var(--accent)" }}>Email</b></span>
            <span style={{ fontSize: "var(--text-meta)", color: "var(--muted)" }}>Current</span>
          </span>

          {/* Another origin, so a real anchor. */}
          <a
            role="menuitem"
            href="https://getpulseassist.com/dashboard"
            className="flex items-center justify-between gap-2 px-2 py-1.5"
            style={{ fontSize: "var(--text-dense)", borderRadius: 4 }}
          >
            <span>PulseAssist <b>AI</b></span>
            <Icon name="external" size={12} style={{ color: "var(--muted)" }} />
          </a>

          <span
            className="mt-1 px-2 pb-1 pt-1.5"
            style={{ fontSize: "var(--text-meta)", color: "var(--muted)", borderTop: "1px solid var(--hairline)" }}
          >
            One ENICE account, one sign-in.
          </span>
        </span>
      )}
    </span>
  );
}

function NotYet({
  children,
  reason,
  className = "",
  style,
}: {
  children: ReactNode;
  reason: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <span
      aria-disabled="true"
      title={`Not built yet — ${reason}`}
      className={`cursor-default select-none ${className}`}
      style={{ color: "var(--muted)", opacity: 0.55, ...style }}
    >
      {children}
    </span>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const { theme, toggle } = useTheme();
  /*
   * Below 900px the rail is an off-canvas drawer. See `.pa-rail` in app.css for why the closed state
   * uses `visibility` and not only `transform`.
   */
  const [navOpen, setNavOpen] = useState(false);

  // Navigating must close it. Otherwise tapping a link on a phone leaves the drawer covering the page
  // it just opened, and the person has to dismiss it to see the thing they asked for.
  useEffect(() => setNavOpen(false), [location]);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setNavOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);

  return (
    <div className="pa-shell">
      {navOpen && (
        <div
          className="pa-backdrop"
          onClick={() => setNavOpen(false)}
          /*
           * Not a button and not focusable: the Escape handler and the close control are the keyboard
           * routes out. A focusable backdrop is a tab stop that announces nothing.
           */
          aria-hidden="true"
        />
      )}
      <aside className="pa-rail" data-open={navOpen ? "true" : "false"}>
        <div
          className="flex h-[52px] items-center gap-[10px] px-4"
          style={{ borderBottom: "1px solid var(--hairline)" }}
        >
          {/*
            The actual trademark, not a coloured square.

            This was a 22px rounded rectangle filled with the ecosystem gradient — a placeholder that
            read as one. A plain swatch where a logo belongs is the single clearest signal that a
            product is not finished, and it sat at the top-left of every screen.

            Now the real mark (public/brand/icon.svg), which is the PulseAssist monogram in the Email
            blue rather than the AI violet — same geometry, sibling colour. The gradient still appears
            here and only here; the difference is that it is now inside the letterform instead of
            standing in for it.
          */}
          {/*
            Inline, not an <img> at /brand/icon.svg — see BrandMark.tsx. `public/` files are copied
            verbatim with no content hash, so that path is cached for an hour and a deploy left the NEW
            markup rendering the OLD artwork. Inline means the mark is versioned with the code that uses
            it, and it costs no request on first paint.
          */}
          <BrandMark size={22} />
          <span style={{ fontSize: "var(--text-dense)" }}>
            <b style={{ fontWeight: 600 }}>PulseAssist</b>{" "}
            {/* Which product, stated. The switcher itself needs a second product to switch TO. */}
            <span style={{ color: "var(--accent)", fontWeight: 500 }}>Email</span>
          </span>
          {/*
            A REAL switcher now. It used to be a NotYet marker that still rendered a chevron — which
            reads as a dropdown and does nothing when clicked, so people click it repeatedly and
            conclude the app is broken. A chevron is a promise; either honour it or remove it.
            ENICE is one platform with two products, and this is how you move between them.
          */}
          <ProductSwitcher />
          {/* The drawer needs a visible way out that is not "guess that the backdrop is tappable". */}
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            aria-label="Close navigation"
            className="pa-mobile-only items-center"
            style={{ color: "var(--muted)" }}
          >
            <Icon name="close" size={16} />
          </button>
        </div>

        <nav id="pa-nav" className="min-h-0 flex-1 overflow-y-auto p-2" aria-label="Sections">
          {NAV.map((group, gi) => (
            <div key={group.title ?? `g${gi}`}>
              {group.title && <div className="micro px-2 pt-[14px] pb-[6px]">{group.title}</div>}
              {group.items.map((item) => {
                const active = location === item.href;
                const base = {
                  height: 32,
                  fontSize: "var(--text-dense)",
                  borderRadius: "var(--radius)",
                } as const;

                const inner = (
                  <>
                    <Icon name={item.icon} style={{ opacity: 0.8 }} />
                    <span className="truncate">{item.label}</span>
                    {item.count !== undefined && (
                      <span className="ml-auto" style={{ fontSize: "var(--text-micro)", color: "var(--muted)" }}>
                        {item.count}
                      </span>
                    )}
                    {item.warn !== undefined && (
                      <span
                        className="ml-auto flex items-center gap-1"
                        style={{ fontSize: "var(--text-micro)", color: "var(--st-deferred)", fontWeight: 500 }}
                      >
                        <Icon name="warning" size={12} />
                        {item.warn}
                      </span>
                    )}
                    {item.external && <Icon name="external" size={12} style={{ marginLeft: "auto", opacity: 0.6 }} />}
                  </>
                );

                if (item.pending) {
                  return (
                    <NotYet
                      key={item.href}
                      reason={item.reason ?? "this screen has not been built"}
                      className="relative flex items-center gap-[10px] px-2"
                      style={base}
                    >
                      {inner}
                    </NotYet>
                  );
                }

                if (item.external) {
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      // A new tab, because these leave the console entirely. Losing an
                      // in-progress screen to a documentation link would be its own small bug.
                      target="_blank"
                      rel="noreferrer noopener"
                      className="relative flex items-center gap-[10px] px-2 no-underline"
                      style={{ ...base, color: "var(--text-2)" }}
                    >
                      {inner}
                    </a>
                  );
                }

                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    aria-current={active ? "page" : undefined}
                    className="relative flex items-center gap-[10px] px-2 no-underline"
                    style={{
                      ...base,
                      color: active ? "var(--accent)" : "var(--text-2)",
                      background: active ? "var(--accent-subtle)" : "transparent",
                      fontWeight: active ? 500 : 400,
                    }}
                  >
                    {active && (
                      <span
                        aria-hidden="true"
                        className="absolute top-[6px] bottom-[6px] -left-2 w-[2px]"
                        style={{ background: "var(--accent)", borderRadius: "0 2px 2px 0" }}
                      />
                    )}
                    {inner}
                  </Link>
                );
              })}
            </div>
          ))}

          {/*
            Settings sits below the divider rather than in a group: it is not one of the jobs the
            groups name (Monitor, Compose, Infrastructure, Account, Developer) — it is where you go to
            change the account itself. Same active treatment as every other link, so the rail has one
            behaviour rather than a special case at the bottom.
          */}
          <div className="mt-[10px] pt-[14px]" style={{ borderTop: "1px solid var(--hairline)" }}>
            {/*
              Settings and Help, in that order.
              
              Both are below the divider for the same reason: neither is one of the jobs the groups name
              (Monitor, Compose, Infrastructure, Account, Developer). Settings is where you change the
              account; Help is where you go when the product has not told you what you needed to know. A
              rail whose bottom section is "everything that is not a job" is easier to scan than one with
              Help wedged into Account.
              
              Rendered from a list rather than as two copies of the same twenty lines — the active
              treatment was already duplicated once here, and duplicating it again is how one of them
              ends up not matching the other.
            */}
            {([
              { href: "/settings", icon: "settings", label: "Settings" },
              { href: "/help", icon: "help", label: "Help" },
            ] as const).map((item) => {
              const active = location === item.href;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  aria-current={active ? "page" : undefined}
                  onClick={() => setNavOpen(false)}
                  className="relative flex items-center gap-[10px] rounded-[var(--radius)] px-2"
                  style={{
                    height: 32,
                    fontSize: "var(--text-dense)",
                    color: active ? "var(--accent)" : "var(--text-2)",
                    background: active ? "var(--accent-subtle)" : "transparent",
                    fontWeight: active ? 500 : 400,
                  }}
                >
                  {active && (
                    <span
                      aria-hidden="true"
                      className="absolute top-[6px] bottom-[6px] -left-2 w-[2px]"
                      style={{ background: "var(--accent)", borderRadius: "0 2px 2px 0" }}
                    />
                  )}
                  <Icon name={item.icon} style={{ opacity: 0.8 }} />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </nav>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-col">
        <header
          className="flex h-[52px] items-center gap-[14px] px-4 sm:px-5"
          style={{ background: "var(--surface)", borderBottom: "1px solid var(--border)" }}
        >
          {/*
           * The only route to navigation below 900px. `aria-expanded` and `aria-controls` are what make
           * it a real disclosure rather than a decorative glyph.
           */}
          <button
            type="button"
            onClick={() => setNavOpen((o) => !o)}
            aria-label="Open navigation"
            aria-expanded={navOpen}
            aria-controls="pa-nav"
            className="pa-mobile-only items-center"
            style={{ color: "var(--text-2)" }}
          >
            <Icon name="menu" size={18} />
          </button>

          {/*
            The brand, on mobile.

            The mark and wordmark live at the top of the rail — which below 900px is a closed off-canvas
            drawer. So on a phone the header was a hamburger and nothing else: the product had no name and
            no mark anywhere on screen, which is the single clearest way to look unfinished. Verified in a
            390px browser before fixing.

            `pa-mobile-only` because above 900px the rail is visible and showing it twice would be worse
            than not showing it at all.
          */}
          {/*
            The MARK only — no wordmark.

            First attempt put the full "PulseAssist Email" lockup here and it made the header worse than
            the problem it fixed: at 390px there is not room for a hamburger, a lockup, the environment
            chip and the account menu, so the wordmark truncated to "PulseAssi…" AND pushed the account
            control down to "Sign…". Seen in a 390px browser, not reasoned about.

            The mark alone is enough: it is distinctive, it is the same mark as the tab icon, and the page
            title directly beneath it already names where you are. Identity restored, nothing truncated.
          */}
          <span className="pa-mobile-only items-center shrink-0">
            <BrandMark size={20} />
          </span>

          {/*
            The workspace is resolved SERVER-SIDE from the session on every /v1 call
            (resolveWorkspaceIdForUser). The console cannot name it — there is no identity endpoint on
            /v1 — and it certainly cannot switch it. Saying so is better than a dropdown that lists
            nothing.
          */}
          {/*
            No chevron. The workspace is resolved server-side per request and /v1 exposes no endpoint
            that names or switches it, so there is nothing to open — and a chevron over nothing is
            worse than no chevron. The label stays because knowing which context you are in is useful;
            the affordance goes because it cannot be honoured.
          */}
          <NotYet reason="/v1 has no endpoint that names or switches the workspace" className="pa-desktop-only flex items-center" style={{ fontSize: "var(--text-dense)" }}>
            Workspace
          </NotYet>

          {/*
            Live, stated as fact rather than offered as a choice. EMAIL_SANDBOX_MODE is a
            SERVER-side switch; until /v1 reports it, a toggle here could only lie.
          */}
          <span
            className="inline-flex h-6 items-center gap-[6px] rounded-full px-[9px]"
            style={{
              border: "1px solid var(--border)",
              color: "var(--text-2)",
              fontSize: "var(--text-meta)",
            }}
            title="This deployment sends live mail. Sandbox is a server-side setting and is not switchable from here."
          >
            <span
              aria-hidden="true"
              className="h-[6px] w-[6px] rounded-full"
              style={{ background: "var(--st-delivered)" }}
            />
            Live
          </span>

          {/*
           * An explicit spacer, because the right-hand chrome used to be pushed over by `ml-auto` on the
           * search box — which meant hiding search on mobile silently un-aligned the theme toggle and the
           * account menu. A spacer keeps the alignment independent of which optional items are visible.
           */}
          <span aria-hidden="true" className="flex-1" />

          {/*
            It told people where to search and then refused to take them there. Now it goes to Logs,
            where the filters actually are. Still not global search — the label says so — but a control
            that does the one useful thing it names beats a dashed box that does nothing.
          */}
          <Link
            href="/logs"
            className="pa-desktop-only flex h-[30px] min-w-[260px] items-center gap-2 px-[10px]"
            style={{
              border: "1px dashed var(--border)",
              borderRadius: "var(--radius)",
              fontSize: "var(--text-dense)",
              background: "var(--canvas)",
              color: "var(--muted)",
            }}
            title="Open Logs, where message search and filters live"
          >
            <Icon name="search" size={14} />
            <span className="flex-1 truncate">Search messages — open Logs</span>
          </Link>

          <button
            type="button"
            onClick={toggle}
            aria-label={theme === "light" ? "Switch to dark theme" : "Switch to light theme"}
            className="flex items-center"
            style={{ color: "var(--muted)" }}
          >
            <Icon name={theme === "light" ? "moon" : "sun"} />
          </button>

          {/*
            The REAL signed-in account, from the session, replacing a hard-coded "PA" avatar that
            implied an identity the console did not know. Showing the wrong person's initials on a tool
            that sends mail on their behalf is not a cosmetic problem.
          */}
          <AccountMenu />
        </header>

        {/*
          Platform announcement banner, above the content and below the header. In normal flow so it
          occupies real layout space rather than overlaying the page. Renders nothing when there is
          no banner for this product, so it costs no vertical space the rest of the time.
        */}
        <AnnouncementBanner />

        {/*
          `pa-measure` caps the content at the 1400px the design direction specifies. It is an inner
          wrapper, not `.pa-main` itself, because `.pa-main` is the scroll container — capping that would
          park the scrollbar in the middle of the window rather than at the window edge.
        */}
        <main className="pa-main">
          <div className="pa-measure">{children}</div>
        </main>
      </div>
    </div>
  );
}

/**
 * Who is signed in, and the way out.
 *
 * The email is shown in full rather than reduced to initials. On a console that sends mail as a
 * business, "which account am I in" is a question with consequences — two people sharing a machine, or
 * one person with a personal and a work account, need to be able to see the answer without clicking.
 */
function AccountMenu() {
  const who = identity();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  // Close on outside click and on Escape. A menu that can only be closed by clicking the trigger again
  // is a menu people leave open by accident.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    const onClick = (e: MouseEvent) => {
      if (!(e.target as HTMLElement)?.closest?.("[data-account-menu]")) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const label = who?.email ?? "Signed in";

  return (
    /*
     * `min-w-0` on both this wrapper and the email below is what lets the header fit a 360px screen.
     * A flex item defaults to `min-width: auto`, so it refuses to shrink below its content — and the
     * content here is a nowrap monospace email address. `truncate` cannot engage without somewhere to
     * shrink to, so the button held its full width and pushed the header 11px past the viewport,
     * scrolling the entire page sideways. `max-w` does not help: it caps the maximum, not the minimum.
     */
    <span className="relative min-w-0" data-account-menu="">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        /*
         * `w-full` matters as much as `min-w-0` here. A <button> sizes to fit-content even when it is
         * `display: flex`, so this one held its 210px max-width and spilled out of a wrapper that had
         * correctly shrunk to 183px — the overflow moved from the header to inside the button and the
         * page still scrolled sideways. Filling the wrapper is what finally lets the email truncate.
         */
        className="flex w-full min-w-0 max-w-[210px] items-center gap-2"
        style={{ fontSize: "var(--text-meta)", color: "var(--text-2)" }}
        title={who?.name ? `${who.name} — ${label}` : label}
      >
        <span
          aria-hidden="true"
          className="grid h-[24px] w-[24px] shrink-0 place-items-center rounded-full"
          style={{ background: "var(--accent)", color: "#fff", fontSize: 10, fontWeight: 600 }}
        >
          {(who?.name ?? who?.email ?? "?").trim().slice(0, 2).toUpperCase()}
        </span>
        <span className="mono min-w-0 truncate">{label}</span>
        <Icon name="chevron-down" size={13} style={{ color: "var(--muted)" }} />
      </button>

      {open && (
        <span
          role="menu"
          className="absolute right-0 z-50 mt-2 flex w-[240px] flex-col overflow-hidden"
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-lg)",
            // z-50 and the modal shadow so the menu sits cleanly above the rail and page rather than
            // being clipped under a sticky header (z-20 could fall behind other floating layers), and
            // reads with a sharp edge instead of the barely-there overlay elevation.
            boxShadow: "var(--shadow-modal)",
          }}
        >
          <span className="px-3 py-[10px]" style={{ borderBottom: "1px solid var(--hairline)" }}>
            {who?.name && (
              <span className="block truncate" style={{ fontSize: "var(--text-dense)", fontWeight: 500 }}>
                {who.name}
              </span>
            )}
            <span className="mono block truncate" style={{ fontSize: "var(--text-meta)", color: "var(--muted)" }}>
              {label}
            </span>
          </span>
          <button
            type="button"
            role="menuitem"
            disabled={signingOut}
            onClick={() => {
              setSigningOut(true);
              // The session module clears locally first and revokes best-effort, so a failed network
              // call still leaves the person signed out of this browser.
              void signOut();
            }}
            className="flex h-9 items-center gap-2 px-3 text-left"
            style={{ fontSize: "var(--text-dense)", color: "var(--text-2)" }}
          >
            <Icon name="lock" size={13} style={{ color: "var(--muted)" }} />
            {signingOut ? "Signing out…" : "Sign out"}
          </button>
        </span>
      )}
    </span>
  );
}

/** Page header. One shape everywhere, so a page title is never styled twice. */
/**
 * The page header: title, an optional line of context, and the page's primary actions.
 *
 * ─── Why `sub` was added ────────────────────────────────────────────────────
 *
 * This took `title` and `actions` only, so all sixteen screens opened with a single 20px word and a
 * button. Seen side by side at 1600px, that is the clearest thing separating this console from a
 * first-tier product: a bare noun tells an operator nothing about what the screen governs, what the
 * numbers on it mean, or what it is safe to do here — and someone being shown the product for the
 * first time has to be told all of that out loud instead of reading it.
 *
 * One line, not a paragraph: it sits on the `--muted` step below a 20px title, so anything longer
 * competes with the content it is introducing.
 *
 * Optional, because two pages (Configuration, Plans) render an early header while loading or
 * unsubscribed, where a description of a screen you cannot see yet is noise.
 */
export function PageHeader({
  title,
  sub,
  actions,
}: {
  title: string;
  sub?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="pa-page-header mb-[14px] flex items-start gap-3">
      <div className="min-w-0">
        <h1 style={{ fontSize: "var(--text-page)", fontWeight: 600, letterSpacing: "-0.01em" }}>{title}</h1>
        {sub && (
          <p
            className="mt-[3px] max-w-[68ch]"
            style={{ fontSize: "var(--text-meta)", color: "var(--muted)", lineHeight: 1.5 }}
          >
            {sub}
          </p>
        )}
      </div>
      {/* `items-start` above with `mt-[2px]` here keeps the action optically aligned to the TITLE
          rather than centred against a two-line block. */}
      {actions && <div className="ml-auto mt-[2px] flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
